import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SendIcon from '@material-ui/icons/Send';
import StopIcon from '@material-ui/icons/Stop';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ReplayIcon from '@material-ui/icons/Replay';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Approach, ChatMessage, Question, SkillSummary } from '../../api/types';
import { groupMessagesIntoTurns } from '../../utils/group-messages';
import { ApproachViewer } from '../ApproachViewer';
import { ConversationTurnGroup } from './ConversationTurnGroup';
import { MarkdownMessage } from './MarkdownMessage';
import { StreamingIndicator } from './StreamingIndicator';
import { PendingInteractionSurface } from '../PendingInteractionSurface';
import { QuestionCard } from '../QuestionCard';

export interface ChatPanelProps {
  messages: ChatMessage[];
  streamingContent: string;
  isStreaming: boolean;
  disabled: boolean;
  disabledPlaceholder?: string;
  emptyStateMessage?: string;
  onSendMessage: (text: string, skillId?: string) => void;
  availableSkills?: SkillSummary[];
  onLoadSkills?: () => void;
  draftText?: string;
  onDraftChange?: (text: string) => void;
  showStartButton?: boolean;
  onStartPhase?: () => void;
  onCliClick?: () => void;
  disableStart?: boolean;
  disableStartTooltip?: string;
  phaseLabel?: string;
  isError?: boolean;
  statusMessage?: string;
  statusTone?: 'active' | 'sync' | 'passive' | 'warning' | 'error';
  statusBusy?: boolean;
  stabilizeInitialLayout?: boolean;
  pendingQuestions?: Question[];
  pendingApproaches?: { question: string; approaches: Approach[] };
  pendingInteractionHint?: string;
  pendingInteractionNotice?: string;
  onAnswerQuestion?: (answers: Record<string, string>) => void;
  onSelectApproach?: (approachTitle: string) => void;
  onOpenPendingInteraction?: () => void;
  hidePendingInteractionInline?: boolean;
  rateLimitInfo?: {
    message: string;
    resetTime?: string;
    timezone?: string;
  };
  enableSmartAutoScroll?: boolean;
  scrollPersistenceKey?: string;
  recoveryState?: string;
  recoveryElapsedMs?: number;
  recoveryRetryCount?: number;
  showReconnectNow?: boolean;
  onReconnectNow?: () => void;
  recoveryActionLabel?: string;
  toolUseSummary?: {
    tools: string[];
    totalEvents: number;
  } | null;
  currentTool?: string;
  toolCount?: number;
  isThinking?: boolean;
  showStreamingIndicator?: boolean;
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  messagesContainer: {
    flex: 1,
    minHeight: '200px',
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    '&::-webkit-scrollbar': {
      width: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 2,
      backgroundColor: theme.palette.divider,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
  },
  messageBubble: {
    maxWidth: '82%',
    padding: theme.spacing(2, 2.5),
    borderRadius: 16,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: 1.7,
    fontSize: '0.875rem',
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRight: `3px solid ${theme.palette.grey[500]}`,
  },
  assistantMessage: {
    alignSelf: 'flex-start',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderLeft: `3px solid ${theme.palette.secondary.main}`,
    whiteSpace: 'normal' as const,
  },
  systemMessage: {
    alignSelf: 'center',
    maxWidth: '90%',
    backgroundColor: theme.palette.warning.light || '#FFF8E1',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `3px solid ${theme.palette.warning.main}`,
    fontSize: '0.7rem',
    fontStyle: 'italic' as const,
    textAlign: 'center' as const,
  },
  streamingMessage: {
    alignSelf: 'flex-start',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderLeft: `3px solid ${theme.palette.secondary.main}`,
  },
  '@keyframes fadeIn': {
    from: { opacity: 0, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  streamingCursor: {
    display: 'inline-block',
    width: 2,
    height: '1em',
    backgroundColor: theme.palette.secondary.main,
    marginLeft: 2,
    verticalAlign: 'text-bottom',
    animation: '$blink 1s step-end infinite',
  },
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
  inputContainer: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  streamingInput: {
    '& input::placeholder, & textarea::placeholder': {
      color: '#201547',
      opacity: 1,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#FA4616',
        borderWidth: 2,
      },
      '&:hover fieldset': {
        borderColor: '#FA4616',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#FA4616',
      },
    },
  },
  disabledInput: {
    opacity: 0.6,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  charCounter: {
    fontSize: '0.6rem',
    color: theme.palette.text.hint,
    textAlign: 'right' as const,
    padding: theme.spacing(0, 1),
    marginTop: 2,
  },
  skillAutocomplete: {
    maxHeight: 200,
    overflow: 'auto',
    zIndex: 1300,
    width: '100%',
  },
  skillChip: {
    marginBottom: 4,
  },
  skillListItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
  },
  sendButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    borderRadius: '50%',
    width: 32,
    height: 32,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.grey[500],
    },
  },
  startContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
  },
  startButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
    padding: theme.spacing(1, 2.5),
    fontSize: '0.8125rem',
    fontWeight: 600,
    textTransform: 'none' as const,
  },
  startDescription: {
    color: theme.palette.text.secondary,
    textAlign: 'center' as const,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: theme.spacing(2),
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    margin: theme.spacing(2),
  },
  errorButton: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.warning.dark,
    },
  },
  statusStrip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 1),
    marginBottom: theme.spacing(0.5),
    borderLeft: `3px solid ${theme.palette.secondary.main}`,
    backgroundColor: 'rgba(250, 70, 22, 0.04)',
    borderRadius: 4,
  },
  statusStripSync: {
    borderLeftColor: theme.palette.grey[500],
    backgroundColor: theme.palette.grey[50],
  },
  statusStripPassive: {
    borderLeftColor: theme.palette.text.hint,
    backgroundColor: theme.palette.grey[50],
  },
  statusStripWarning: {
    borderLeftColor: theme.palette.warning.main,
    backgroundColor: theme.palette.warning.light || '#FFF8E1',
  },
  statusStripError: {
    borderLeftColor: theme.palette.error.main,
    backgroundColor: theme.palette.error.light || '#FFEBEE',
  },
  statusStripText: {
    fontSize: '0.68rem',
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  statusTextContent: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    minWidth: 0,
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: theme.palette.secondary.main,
    display: 'inline-block',
    flexShrink: 0,
    marginRight: theme.spacing(0.75),
  },
  statusDotSync: {
    backgroundColor: theme.palette.grey[500],
  },
  statusDotPassive: {
    backgroundColor: theme.palette.text.hint,
  },
  statusDotWarning: {
    backgroundColor: theme.palette.warning.main,
  },
  statusDotError: {
    backgroundColor: theme.palette.error.main,
  },
  statusDotBusy: {
    animation: '$statusPulse 1.2s ease-in-out infinite',
  },
  '@keyframes statusPulse': {
    '0%, 100%': { opacity: 0.45, transform: 'scale(0.85)' },
    '50%': { opacity: 1, transform: 'scale(1)' },
  },
  statusStripButton: {
    fontSize: '0.65rem',
    color: theme.palette.secondary.main,
    whiteSpace: 'nowrap' as const,
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0, 0.5),
    minWidth: 'auto',
  },
  topSlot: {
    minHeight: theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    flexShrink: 0,
  },
  topSlotSpacer: {
    minHeight: theme.spacing(5),
    width: '100%',
    visibility: 'hidden',
  },
  emptyStateSlot: {
    flex: 1,
    minHeight: theme.spacing(28),
    display: 'flex',
    flexDirection: 'column',
  },
  rateLimitAlert: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    margin: theme.spacing(1, 0),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.warning.light || '#FFF3E0',
    border: `1px solid ${theme.palette.warning.main}`,
    color: theme.palette.text.primary,
  },
  rateLimitIcon: {
    color: theme.palette.warning.main,
    marginTop: 2,
  },
  rateLimitText: {
    fontSize: '0.72rem',
    lineHeight: 1.5,
  },
  rateLimitTime: {
    fontWeight: 600,
    color: theme.palette.warning.dark || '#E65100',
  },
}));

function getPlaceholder(
  disabled: boolean,
  isStreaming: boolean,
  disabledPlaceholder?: string,
): string {
  if (disabled)
    return disabledPlaceholder ?? 'Fase concluída - somente leitura';
  if (isStreaming) return 'Enviar mensagem interrompe a geração atual...';
  return 'Digite sua mensagem...';
}

function getEmptyStateMessage(
  disabled: boolean,
  disabledPlaceholder: string | undefined,
  emptyStateMessage: string | undefined,
  showStartButton: boolean,
): string {
  if (emptyStateMessage !== undefined) {
    return emptyStateMessage;
  }

  if (disabled) {
    return disabledPlaceholder ?? 'Fase concluída - somente leitura';
  }

  if (showStartButton) {
    return 'Clique para iniciar o agente IA nesta fase';
  }

  return 'Envie uma mensagem para iniciar a conversa';
}

function getMessageSignature(messages: ChatMessage[]): string {
  if (messages.length === 0) return '';
  const last = messages[messages.length - 1];
  return `${messages.length}:${last.id}:${last.timestamp}`;
}

const SCROLL_BOTTOM_THRESHOLD_PX = 80;
const CHAT_SCROLL_STORAGE_PREFIX = 'spassu-code:chat-scroll';

function formatToolUseMetaLine(
  toolUseSummary: ChatPanelProps['toolUseSummary'],
): string | null {
  if (!toolUseSummary || toolUseSummary.totalEvents <= 0) {
    return null;
  }

  const suffix = toolUseSummary.totalEvents === 1 ? 'evento' : 'eventos';
  return `Ferramentas: ${toolUseSummary.tools.join(', ')} (${
    toolUseSummary.totalEvents
  } ${suffix})`;
}

export const ChatPanel = ({
  messages,
  streamingContent,
  isStreaming,
  disabled,
  disabledPlaceholder,
  onSendMessage,
  showStartButton,
  onStartPhase,
  onCliClick,
  disableStart,
  disableStartTooltip,
  phaseLabel,
  isError,
  statusMessage,
  statusTone = 'active',
  statusBusy = false,
  stabilizeInitialLayout = false,
  pendingQuestions,
  pendingApproaches,
  pendingInteractionHint,
  pendingInteractionNotice,
  onAnswerQuestion,
  onSelectApproach,
  onOpenPendingInteraction,
  hidePendingInteractionInline = false,
  rateLimitInfo,
  draftText,
  onDraftChange,
  enableSmartAutoScroll = false,
  scrollPersistenceKey,
  recoveryState,
  recoveryElapsedMs,
  recoveryRetryCount,
  showReconnectNow = false,
  onReconnectNow,
  recoveryActionLabel,
  toolUseSummary,
  currentTool,
  toolCount,
  isThinking,
  showStreamingIndicator = true,
  availableSkills,
  onLoadSkills,
  emptyStateMessage,
}: ChatPanelProps) => {
  const classes = useStyles();
  const [uncontrolledInputText, setUncontrolledInputText] = useState('');
  const [selectedSkill, setSelectedSkill] = useState<SkillSummary | null>(null);
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const inputAnchorRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number>(0);
  const isPinnedToBottomRef = useRef(true);
  const restoredScrollForKeyRef = useRef<string | null>(null);
  const isDraftControlled = draftText !== undefined;
  const inputText = isDraftControlled ? draftText ?? '' : uncontrolledInputText;
  const messageSignature = getMessageSignature(messages);
  const hasRenderableChatContent =
    messages.length > 0 || Boolean(streamingContent) || isStreaming;
  const [expandedTurns, setExpandedTurns] = useState<Set<string>>(new Set());
  const toggleTurn = useCallback((turnId: string) => {
    setExpandedTurns(prev => {
      const next = new Set(prev);
      if (next.has(turnId)) next.delete(turnId);
      else next.add(turnId);
      return next;
    });
  }, []);
  const turns = useMemo(() => groupMessagesIntoTurns(messages), [messages]);
  const scrollPersistenceStorageKey = scrollPersistenceKey
    ? `${CHAT_SCROLL_STORAGE_PREFIX}:${scrollPersistenceKey}`
    : null;
  const resolvedEmptyStateMessage = getEmptyStateMessage(
    disabled,
    disabledPlaceholder,
    emptyStateMessage,
    Boolean(showStartButton && onStartPhase && !isError),
  );

  const updateInputText = useCallback(
    (value: string) => {
      if (!isDraftControlled) {
        setUncontrolledInputText(value);
      }
      onDraftChange?.(value);
    },
    [isDraftControlled, onDraftChange],
  );

  const syncPinnedState = useCallback((el: HTMLElement) => {
    const isNearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <
      SCROLL_BOTTOM_THRESHOLD_PX;
    isPinnedToBottomRef.current = isNearBottom;
  }, []);

  const persistScrollPosition = useCallback(
    (el: HTMLElement) => {
      if (!scrollPersistenceStorageKey) {
        return;
      }

      try {
        sessionStorage.setItem(
          scrollPersistenceStorageKey,
          String(el.scrollTop),
        );
      } catch {
        // Ignore sessionStorage errors in restricted environments.
      }
    },
    [scrollPersistenceStorageKey],
  );

  useEffect(() => {
    restoredScrollForKeyRef.current = null;
  }, [scrollPersistenceStorageKey]);

  useEffect(() => {
    if (
      !enableSmartAutoScroll ||
      !scrollPersistenceStorageKey ||
      !hasRenderableChatContent
    ) {
      return;
    }

    if (restoredScrollForKeyRef.current === scrollPersistenceStorageKey) {
      return;
    }

    const el = messagesContainerRef.current;
    if (!el) {
      return;
    }

    restoredScrollForKeyRef.current = scrollPersistenceStorageKey;

    try {
      const persistedTopRaw = sessionStorage.getItem(
        scrollPersistenceStorageKey,
      );
      if (persistedTopRaw === null || persistedTopRaw === undefined) {
        syncPinnedState(el);
        return;
      }

      const persistedTop = Number(persistedTopRaw);
      if (Number.isNaN(persistedTop)) {
        syncPinnedState(el);
        return;
      }

      requestAnimationFrame(() => {
        const targetEl = messagesContainerRef.current;
        if (!targetEl) {
          return;
        }

        const maxScrollTop = Math.max(
          0,
          targetEl.scrollHeight - targetEl.clientHeight,
        );
        const nextTop = Math.min(Math.max(persistedTop, 0), maxScrollTop);
        targetEl.scrollTop = nextTop;
        syncPinnedState(targetEl);
      });
    } catch {
      syncPinnedState(el);
    }
  }, [
    enableSmartAutoScroll,
    hasRenderableChatContent,
    messageSignature,
    scrollPersistenceStorageKey,
    syncPinnedState,
  ]);

  // Auto-scroll only when user is already near the bottom of the chat.
  // Uses requestAnimationFrame to coalesce rapid streaming updates.
  const scrollToBottom = useCallback(() => {
    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
    }
    scrollRafRef.current = requestAnimationFrame(() => {
      const el = messagesContainerRef.current;
      if (!el) return;

      if (enableSmartAutoScroll) {
        if (!isPinnedToBottomRef.current) {
          return;
        }
        el.scrollTop = el.scrollHeight;
        isPinnedToBottomRef.current = true;
        persistScrollPosition(el);
        return;
      }

      const isNearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight <
        SCROLL_BOTTOM_THRESHOLD_PX;
      if (isNearBottom) {
        el.scrollTop = el.scrollHeight;
        persistScrollPosition(el);
      }
    });
  }, [enableSmartAutoScroll, persistScrollPosition]);

  useEffect(() => {
    if (!hasRenderableChatContent) {
      return;
    }
    scrollToBottom();
  }, [hasRenderableChatContent, messageSignature, scrollToBottom]);

  // Throttled scroll during streaming to avoid reflow on every chunk
  useEffect(() => {
    if (!isStreaming) {
      return undefined;
    }
    const id = setInterval(() => {
      scrollToBottom();
    }, 150);
    return () => clearInterval(id);
  }, [isStreaming, scrollToBottom]);

  // Force scroll to bottom when new messages arrive (user sent or full response persisted)
  const prevMessageSnapshotRef = useRef({
    count: messages.length,
    signature: messageSignature,
  });
  useEffect(() => {
    if (enableSmartAutoScroll) {
      prevMessageSnapshotRef.current = {
        count: messages.length,
        signature: messageSignature,
      };
      return;
    }

    if (!hasRenderableChatContent) {
      prevMessageSnapshotRef.current = {
        count: messages.length,
        signature: messageSignature,
      };
      return;
    }

    const hadMessageCountIncrease =
      messages.length > prevMessageSnapshotRef.current.count;
    const hasPersistedMessageContentChanged =
      messageSignature !== prevMessageSnapshotRef.current.signature;

    if (hadMessageCountIncrease || hasPersistedMessageContentChanged) {
      const el = messagesContainerRef.current;
      if (el) {
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight;
          isPinnedToBottomRef.current = true;
          persistScrollPosition(el);
        });
      }
    }
    prevMessageSnapshotRef.current = {
      count: messages.length,
      signature: messageSignature,
    };
  }, [
    enableSmartAutoScroll,
    hasRenderableChatContent,
    messageSignature,
    messages.length,
    persistScrollPosition,
  ]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    return () => {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }

      if (container) {
        persistScrollPosition(container);
      }
    };
  }, [persistScrollPosition]);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) {
      return;
    }

    syncPinnedState(el);
    persistScrollPosition(el);
  }, [persistScrollPosition, syncPinnedState]);

  const handleSend = useCallback(() => {
    const trimmed = inputText.trim();
    if (!trimmed || disabled) return;
    onSendMessage(trimmed, selectedSkill?.id);
    updateInputText('');
    setSelectedSkill(null);
    setShowSkillDropdown(false);
  }, [inputText, disabled, onSendMessage, updateInputText, selectedSkill]);

  const handleInputChange = useCallback(
    (value: string) => {
      updateInputText(value);
      // Detect "/" at start of input to show skill autocomplete
      if (value.startsWith('/')) {
        // Load skills if not loaded yet
        if (
          onLoadSkills &&
          (!availableSkills || availableSkills.length === 0)
        ) {
          onLoadSkills();
        }
        if (availableSkills && availableSkills.length > 0) {
          setShowSkillDropdown(true);
        }
        // If skills are loading, dropdown will open via useEffect below
      } else {
        setShowSkillDropdown(false);
      }
    },
    [updateInputText, availableSkills, onLoadSkills],
  );

  // Show dropdown when skills load after "/" was typed (async load completes)
  useEffect(() => {
    if (
      inputText.startsWith('/') &&
      availableSkills &&
      availableSkills.length > 0 &&
      !showSkillDropdown
    ) {
      setShowSkillDropdown(true);
    }
  }, [availableSkills, inputText, showSkillDropdown]);

  const handleSelectSkill = useCallback(
    (skill: SkillSummary) => {
      setSelectedSkill(skill);
      setShowSkillDropdown(false);
      updateInputText('');
    },
    [updateInputText],
  );

  const handleClearSkill = useCallback(() => {
    setSelectedSkill(null);
  }, []);

  // Load skills when input is focused
  const handleInputFocus = useCallback(() => {
    if (onLoadSkills && (!availableSkills || availableSkills.length === 0)) {
      onLoadSkills();
    }
  }, [onLoadSkills, availableSkills]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const hasContent = messages.length > 0 || streamingContent;
  const shouldReserveTopSlot =
    stabilizeInitialLayout && (Boolean(statusMessage) || !hasContent);
  const shouldUseEmptyStateSlot =
    stabilizeInitialLayout && !hasContent && !isError;
  const recoveryMetaLine =
    recoveryState && recoveryElapsedMs !== undefined
      ? `Estado: ${recoveryState} | Tempo: ${Math.max(
          0,
          Math.floor(recoveryElapsedMs / 1000),
        )}s${
          recoveryRetryCount !== undefined
            ? ` | Tentativas: ${recoveryRetryCount}`
            : ''
        }`
      : null;
  const toolUseMetaLine = formatToolUseMetaLine(toolUseSummary);
  const statusStripSegments = [
    statusMessage,
    recoveryMetaLine,
    toolUseMetaLine,
  ].filter(Boolean);
  const statusStripClassName = [
    classes.statusStrip,
    statusTone === 'sync' ? classes.statusStripSync : '',
    statusTone === 'passive' ? classes.statusStripPassive : '',
    statusTone === 'warning' ? classes.statusStripWarning : '',
    statusTone === 'error' ? classes.statusStripError : '',
  ]
    .filter(Boolean)
    .join(' ');
  const statusDotClassName = [
    classes.statusDot,
    statusTone === 'sync' ? classes.statusDotSync : '',
    statusTone === 'passive' ? classes.statusDotPassive : '',
    statusTone === 'warning' ? classes.statusDotWarning : '',
    statusTone === 'error' ? classes.statusDotError : '',
    statusBusy ? classes.statusDotBusy : '',
  ]
    .filter(Boolean)
    .join(' ');
  const renderStatusSegment = (
    value: string | null | undefined,
    key: string,
    needsSeparator: boolean,
  ) => {
    if (!value) {
      return null;
    }

    return (
      <Fragment key={key}>
        {needsSeparator ? <span>{' \u2014 '}</span> : null}
        <span>{value}</span>
      </Fragment>
    );
  };
  const statusStripNode =
    statusStripSegments.length > 0 ? (
      <div className={statusStripClassName} role="status">
        <Typography variant="caption" className={classes.statusStripText}>
          <span className={statusDotClassName} aria-hidden="true" />
          <span className={classes.statusTextContent}>
            {renderStatusSegment(statusMessage, 'status', false)}
            {renderStatusSegment(
              recoveryMetaLine,
              'recovery',
              Boolean(statusMessage),
            )}
            {renderStatusSegment(
              toolUseMetaLine,
              'tool-use',
              Boolean(statusMessage || recoveryMetaLine),
            )}
          </span>
        </Typography>
        {showReconnectNow && onReconnectNow ? (
          <Button
            size="small"
            className={classes.statusStripButton}
            onClick={onReconnectNow}
          >
            {recoveryActionLabel ?? 'Reconectar'}
          </Button>
        ) : null}
      </div>
    ) : null;

  return (
    <div className={classes.root}>
      <div
        className={classes.messagesContainer}
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
      >
        {shouldReserveTopSlot ? (
          <div className={classes.topSlot}>
            <div className={classes.topSlotSpacer} aria-hidden="true" />
          </div>
        ) : null}

        {shouldUseEmptyStateSlot ? (
          <div className={classes.emptyStateSlot}>
            {showStartButton && onStartPhase && !isError ? (
              <div className={classes.startContainer}>
                <Tooltip
                  title={
                    disableStart && disableStartTooltip
                      ? disableStartTooltip
                      : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      className={classes.startButton}
                      startIcon={<PlayArrowIcon />}
                      onClick={onStartPhase}
                      disabled={disableStart}
                      aria-label={`Iniciar fase ${phaseLabel}`}
                    >
                      Iniciar {phaseLabel}
                    </Button>
                  </span>
                </Tooltip>
                {onCliClick && (
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon style={{ fontSize: 14 }} />}
                    onClick={onCliClick}
                    style={{ marginLeft: 12 }}
                  >
                    Usar via CLI
                  </Button>
                )}
              </div>
            ) : null}
            {!hasContent &&
            !isStreaming &&
            !isThinking &&
            resolvedEmptyStateMessage !== '' ? (
              <Typography variant="body2" className={classes.startDescription}>
                {resolvedEmptyStateMessage}
              </Typography>
            ) : null}
          </div>
        ) : (
          <>
            {!hasContent && showStartButton && onStartPhase && !isError && (
              <div className={classes.startContainer}>
                <Tooltip
                  title={
                    disableStart && disableStartTooltip
                      ? disableStartTooltip
                      : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      className={classes.startButton}
                      startIcon={<PlayArrowIcon />}
                      onClick={onStartPhase}
                      disabled={disableStart}
                      aria-label={`Iniciar fase ${phaseLabel}`}
                    >
                      Iniciar {phaseLabel}
                    </Button>
                  </span>
                </Tooltip>
                {onCliClick && (
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon style={{ fontSize: 14 }} />}
                    onClick={onCliClick}
                    style={{ marginLeft: 12 }}
                  >
                    Usar via CLI
                  </Button>
                )}
              </div>
            )}
            {!hasContent &&
            !isStreaming &&
            !isThinking &&
            resolvedEmptyStateMessage !== '' ? (
              <Typography variant="body2" className={classes.startDescription}>
                {resolvedEmptyStateMessage}
              </Typography>
            ) : null}
          </>
        )}

        {turns.map((turn, index) => (
          <ConversationTurnGroup
            key={turn.id}
            turn={turn}
            isLatest={index === turns.length - 1}
            isCollapsed={
              !expandedTurns.has(turn.id) && index < turns.length - 3
            }
            onToggleCollapse={() => toggleTurn(turn.id)}
            messageBubbleClass={classes.messageBubble}
            userMessageClass={classes.userMessage}
            assistantMessageClass={classes.assistantMessage}
            systemMessageClass={classes.systemMessage}
          />
        ))}

        {streamingContent ? (
          <div
            className={`${classes.messageBubble} ${classes.streamingMessage}`}
            style={{ whiteSpace: 'normal' }}
          >
            <MarkdownMessage
              content={streamingContent}
              isStreaming={isStreaming}
            />
            {isStreaming && (
              <span className={classes.streamingCursor} aria-hidden="true" />
            )}
          </div>
        ) : null}

        {showStreamingIndicator ? (
          <StreamingIndicator
            isStreaming={isStreaming}
            isThinking={isThinking}
            currentTool={currentTool}
            toolCount={toolCount ?? 0}
          />
        ) : null}

        {!hidePendingInteractionInline &&
          pendingQuestions &&
          pendingQuestions.length > 0 && (
            <Box style={{ marginTop: 16, marginBottom: 16 }}>
              <PendingInteractionSurface
                title="O agente precisa de informações"
                helperText={pendingInteractionHint}
                noticeText={pendingInteractionNotice}
                primaryActionLabel={
                  onOpenPendingInteraction ? 'Abrir em destaque' : undefined
                }
                onPrimaryAction={onOpenPendingInteraction}
              >
                <QuestionCard
                  questions={pendingQuestions}
                  onSubmit={answers => onAnswerQuestion?.(answers)}
                  disabled={disabled}
                />
              </PendingInteractionSurface>
            </Box>
          )}

        {!hidePendingInteractionInline &&
          pendingApproaches &&
          pendingApproaches.approaches.length > 0 && (
            <Box style={{ marginTop: 16, marginBottom: 16 }}>
              <PendingInteractionSurface
                title="Escolha uma abordagem"
                helperText={pendingInteractionHint}
                noticeText={pendingInteractionNotice}
                primaryActionLabel={
                  onOpenPendingInteraction ? 'Abrir em destaque' : undefined
                }
                onPrimaryAction={onOpenPendingInteraction}
              >
                <ApproachViewer
                  question={pendingApproaches.question}
                  approaches={pendingApproaches.approaches}
                  onSelect={onSelectApproach}
                  disabled={disabled}
                />
              </PendingInteractionSurface>
            </Box>
          )}

        {isError && onStartPhase && (
          <div className={classes.errorContainer}>
            <Typography variant="body2" className={classes.startDescription}>
              Ocorreu um erro nesta fase. Clique para tentar novamente.
            </Typography>
            <Button
              variant="contained"
              className={classes.errorButton}
              startIcon={<ReplayIcon />}
              onClick={onStartPhase}
              aria-label="Reiniciar fase"
            >
              Reiniciar Fase
            </Button>
          </div>
        )}

        {rateLimitInfo && (
          <div className={classes.rateLimitAlert}>
            <ScheduleIcon className={classes.rateLimitIcon} />
            <div>
              <Typography variant="body2" className={classes.rateLimitText}>
                {rateLimitInfo.message}
              </Typography>
              {rateLimitInfo.resetTime && (
                <Typography variant="body2" className={classes.rateLimitText}>
                  Disponível novamente às{' '}
                  <span className={classes.rateLimitTime}>
                    {rateLimitInfo.resetTime}
                  </span>
                  {rateLimitInfo.timezone && ` (${rateLimitInfo.timezone})`}
                </Typography>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={classes.inputContainer}>
        {statusStripNode}
        {selectedSkill && (
          <Chip
            size="small"
            label={`Skill: ${selectedSkill.name}`}
            onDelete={handleClearSkill}
            color="secondary"
            className={classes.skillChip}
          />
        )}
        <div ref={inputAnchorRef}>
          <TextField
            id="chat-input"
            fullWidth
            variant="outlined"
            size="small"
            className={(() => {
              if (isStreaming) {
                return classes.streamingInput;
              }
              if (disabled) {
                return classes.disabledInput;
              }
              return undefined;
            })()}
            placeholder={
              selectedSkill
                ? `Mensagem com skill "${selectedSkill.name}"...`
                : getPlaceholder(disabled, isStreaming, disabledPlaceholder)
            }
            value={inputText}
            onChange={e => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            disabled={disabled}
            multiline
            maxRows={10}
            inputProps={{ style: { fontSize: '0.75rem' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSend}
                    disabled={disabled || !inputText.trim()}
                    size="small"
                    className={classes.sendButton}
                    aria-label={
                      isStreaming ? 'Interromper e enviar' : 'Enviar mensagem'
                    }
                  >
                    {isStreaming ? (
                      <StopIcon fontSize="small" />
                    ) : (
                      <SendIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
        {showSkillDropdown && availableSkills && availableSkills.length > 0 && (
          <ClickAwayListener onClickAway={() => setShowSkillDropdown(false)}>
            <Paper className={classes.skillAutocomplete} elevation={4}>
              <List dense>
                {availableSkills
                  .filter(s => {
                    const query = inputText.slice(1).toLowerCase();
                    return !query || s.name.toLowerCase().includes(query);
                  })
                  .map(skill => (
                    <ListItem
                      key={skill.id}
                      className={classes.skillListItem}
                      onClick={() => handleSelectSkill(skill)}
                    >
                      <ListItemText
                        primary={`/${skill.name}`}
                        secondary={skill.description ?? skill.phase ?? ''}
                      />
                    </ListItem>
                  ))}
              </List>
            </Paper>
          </ClickAwayListener>
        )}
        {inputText.length > 100 && (
          <Typography className={classes.charCounter}>
            {inputText.length} caracteres
          </Typography>
        )}
      </div>
    </div>
  );
};
